/**
 * The row button of a picker's browse lists — folder rows and place entries
 * alike — so a row reads the same in every column:
 *
 *   SELECTED (blue)  the item the dialog will act on.
 *   CURRENT  (grey)  the folder you are in, seen from its parent column — the
 *                    same "you are here" tone as the active place in Places.
 *                    Brought into view when it becomes current, so a long
 *                    parent list still shows where you are.
 *
 * Both can hold at once (Save As selects the folder it is in); blue wins.
 */
import { useCallback, useEffect, useRef, type MouseEvent, type ReactNode } from 'react'

/**
 * Where the latest single click inside a picker landed. The browser fires
 * `dblclick` on whatever is under the pointer at the SECOND click — and when
 * the first click opened a folder, that is a row of the new folder the person
 * never clicked. So a double-click counts only on what its first click hit.
 */
let firstClickTarget: EventTarget | null = null

/** Note a click as the possible first of a double-click; the picker calls this for every click inside it. */
export function noteFirstClick(e: MouseEvent): void {
  if (e.detail <= 1) firstClickTarget = e.target
}

/** Let go of the noted element when a picker closes (it would keep the closed dialog's DOM alive). */
export function forgetFirstClick(): void {
  firstClickTarget = null
}

function isOwnDoubleClick(e: MouseEvent<Element>): boolean {
  return firstClickTarget instanceof Node && e.currentTarget.contains(firstClickTarget)
}

/**
 * The second click of a double-click on THIS element — to be taken with the
 * first as one. Not a fast click on another row: a touch screen's tap counter
 * spans neighbouring rows, and a quick run of taps down a list must tick each.
 */
export function isSecondClickOnSelf(e: MouseEvent<Element>): boolean {
  return e.detail > 1 && isOwnDoubleClick(e)
}

/**
 * Longer than the gap between the two clicks of a double-click, shorter than
 * reacting to new content. A deliberate click on a row that just appeared
 * where the pointer already was, and the second half of a very slow
 * double-click, look the same; past this window the click is taken as meant.
 * The damaging half — a double-click CONFIRMING that row — is refused exactly
 * (`isOwnDoubleClick`), whatever the timing.
 */
const STRAY_CLICK_MS = 400

/**
 * Tells the second click of a double-click that landed on something the FIRST
 * click brought under the pointer (a row of the folder it opened, a list that
 * moved). Only such a click is stray: one on an element that was already
 * there — or any click once it has been up long enough to be aimed at — is
 * someone clicking, however fast.
 */
function useStrayClick(): (e: MouseEvent) => boolean {
  const shownAt = useRef(0)
  useEffect(() => { shownAt.current = Date.now() }, [])
  return useCallback((e: MouseEvent) => e.detail > 1 && Date.now() - shownAt.current < STRAY_CLICK_MS, [])
}

export function PickerRowButton({
  selected, current = false, pressed, toggles = false, onClick, onDoubleClick, testId, children,
}: {
  selected: boolean
  current?: boolean
  /** The row is itself a choice (a picker without ticks): whether it is chosen. */
  pressed?: boolean
  /** A click adds or takes out its tick (a file row in checkbox mode). */
  toggles?: boolean
  onClick: () => void
  onDoubleClick?: () => void
  testId?: string
  children: ReactNode
}) {
  const ref = useRef<HTMLButtonElement>(null)
  const isStray = useStrayClick()
  useEffect(() => {
    if (current) ref.current?.scrollIntoView?.({ block: 'nearest' })
  }, [current])

  const tone = selected
    ? 'bg-blue-600/30 themed-text'
    : current
      ? 'themed-bg-tertiary themed-text'
      : 'themed-text-secondary hover:themed-text hover:themed-bg-tertiary'
  return (
    <button
      ref={ref}
      type="button"
      // Stop here: in the parent column, a click on the pane's empty space goes up a level.
      // A row that IS a choice, or whose click ticks it, takes a double-click's
      // clicks as one: the second would undo the first (the double-click
      // itself opens or confirms).
      onClick={(e) => { e.stopPropagation(); if (!isStray(e) && !((pressed !== undefined || toggles) && isSecondClickOnSelf(e))) onClick() }}
      onDoubleClick={onDoubleClick && ((e) => { e.stopPropagation(); if (isOwnDoubleClick(e)) onDoubleClick() })}
      aria-current={current ? 'location' : undefined}
      aria-pressed={pressed}
      data-testid={testId}
      className={`flex items-center gap-2 w-full text-left py-1.5 px-2 rounded text-sm transition-colors ${tone}`}
    >
      {children}
    </button>
  )
}
